import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { log, notificationService, t } from "../../../../../../services";
import { campaignCategoriesOptions, strCode } from "../../../../../../consts";

import { dateUtil } from "../../../../../../utils";

import {
  DateInput,
  DropdownInput,
  FlexContainers,
  Input,
  TimeInput,
  TooltipIcon,
  WrapperContainer
} from "../../../../../../components";
import { InputLabel } from "./styled";
import { SectionTitle, SectionWrapper } from "../../styled";
import { MessagePreview } from "../../../../components/MessagePreview/MessagePreview";

const OverviewSection = forwardRef(({ overviewData, isEdit }, ref) => {
  const notifications = useSelector((state) => state.notifications.notifications);

  const [campaignName, setCampaignName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedNotificationMessage, setSelectedNotificationMessage] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const [isValid, setIsValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState(null);

  const notificationMessagesOptions = useMemo(() => {
    return (
      notifications?.map((notification) => {
        return {
          id: notification.notificationName,
          value: notification.notificationName
        };
      }) || []
    );
  }, [notifications]);

  const minStartTime = useMemo(() => {
    if (startDate && dateUtil.isToday(startDate)) {
      return dateUtil.getUTCDate();
    }

    return null;
  }, [startDate, startTime]);

  const getOverviewSectionData = useCallback(() => {
    return { campaignName, startDate, startTime, selectedCategory, selectedNotificationMessage };
  }, [campaignName, startDate, startTime, selectedCategory, selectedNotificationMessage]);

  const prepopulateData = () => {
    if (overviewData) {
      setCampaignName(overviewData.campaignName);
      setSelectedCategory(overviewData.selectedCategory);
      setSelectedNotificationMessage(overviewData.selectedNotificationMessage);
      setStartDate(overviewData.startDate);
      setStartTime(overviewData.startTime);
    }
  };

  const resetFormData = () => {
    setCampaignName("");
    setSelectedCategory(null);
    setSelectedNotificationMessage(null);
    setStartDate(null);
    setStartTime(null);

    setIsValid(true);
    setShowErrors(false);
    setMissingFields(null);
  };

  useEffect(() => {
    prepopulateData();

    return () => {
      resetFormData();
    };
  }, [overviewData]);

  useImperativeHandle(ref, () => ({
    saveOverviewSectionData(validate = true) {
      if (!showErrors && validate && !isEdit) {
        setShowErrors(true);
      }

      if (!isValid && validate && !isEdit) {
        log.warn("OverviewSection: Unable to save overview section due to missing fields");

        notificationService.showError({
          title: t(strCode.MISSING_FIELDS),
          message: t(strCode.MISSING_FIELDS_MESSAGE)
        });

        return;
      }

      return getOverviewSectionData();
    },

    resetOverviewSection() {
      resetFormData();
    }
  }));

  useEffect(() => {
    const valid =
      campaignName.length >= 5 &&
      !!startDate &&
      !!startTime &&
      !!selectedCategory &&
      !!selectedNotificationMessage;

    const errorFields = {
      campaignName: campaignName.length < 5, //Space is not allowed in campaign name
      startDate: !startDate,
      startTime: !startTime,
      selectedCategory: !selectedCategory,
      selectedNotificationMessage: !selectedNotificationMessage
    };

    setMissingFields(errorFields);

    setIsValid(valid);
  }, [campaignName, startDate, startTime, selectedCategory, selectedNotificationMessage]);

  useEffect(() => {
    if (minStartTime && startTime && !isEdit) {
      const minDate = new Date(minStartTime);

      minDate.setSeconds(0);
      minDate.setMilliseconds(0);

      if (minDate.getTime() > startTime.getTime()) {
        setStartTime(null);
      }
    }
  }, [minStartTime, startTime, isEdit]);

  return (
    <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={16} alignItems="start">
      <SectionTitle>{t(strCode.OVERVIEW)}</SectionTitle>

      <SectionWrapper>
        <Input
          id="campaignName"
          title={t(strCode.CAMPAIGN_NAME_TITLE)}
          placeholder={t(strCode.CAMPAIGN_NAME_TITLE)}
          warningMsg={t(strCode.CAMPAIGN_NAME_WARNING)}
          value={campaignName}
          setValue={setCampaignName}
          maxLength={36}
          hasError={showErrors && missingFields?.campaignName}
          disabled={isEdit}
        />

        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems="start"
          width="100%"
          gap={8}>
          <InputLabel>{t(strCode.CATEGORY_TITLE)}</InputLabel>

          <DropdownInput
            id="category-selector"
            placeholder={t(strCode.SELECT_MESSAGE_PLACEHOLDER)}
            options={campaignCategoriesOptions}
            selectedOption={selectedCategory}
            onSelectOption={(id) => {
              setSelectedCategory(campaignCategoriesOptions.find((opt) => opt.id === id));
            }}
            hasError={showErrors && missingFields?.selectedCategory}
          />
        </WrapperContainer>
      </SectionWrapper>

      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems="start"
        width="100%"
        gap={8}>
        <WrapperContainer Container={FlexContainers.FlexRowStart}>
          <InputLabel>{t(strCode.NOTIFICATION_MESSAGE_TITLE)}</InputLabel>
          <WrapperContainer pl={5}>
            <TooltipIcon>{t(strCode.NOTIFICATIONS_MESSAGE_HINT)}</TooltipIcon>
          </WrapperContainer>
        </WrapperContainer>

        <DropdownInput
          id="notification-message-selector"
          placeholder={t(strCode.SELECT_MESSAGE_PLACEHOLDER)}
          options={notificationMessagesOptions}
          selectedOption={selectedNotificationMessage}
          onSelectOption={(id) => {
            setSelectedNotificationMessage(
              notificationMessagesOptions.find((opt) => opt.id === id)
            );
          }}
          hasError={showErrors && missingFields?.selectedNotificationMessage}
        />
      </WrapperContainer>

      {selectedNotificationMessage && (
        <MessagePreview
          notification={notifications?.find(
            (notification) => notification.notificationName === selectedNotificationMessage.id
          )}
        />
      )}

      <SectionWrapper>
        <DateInput
          id="start-date"
          title={t(strCode.START_DATE_TITLE)}
          value={startDate}
          setValue={setStartDate}
          minDate={dateUtil.getUTCDate()}
          hasError={showErrors && missingFields?.startDate}
          noTime
        />

        <TimeInput
          id={"start-time"}
          title={t(strCode.START_TIME_TITLE)}
          value={startTime}
          setValue={setStartTime}
          minDate={minStartTime}
          hasError={showErrors && missingFields?.startTime}
        />
      </SectionWrapper>
    </WrapperContainer>
  );
});

OverviewSection.displayName = "OverviewSection";

OverviewSection.propTypes = {
  overviewData: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    selectedCategory: PropTypes.shape({
      accessRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }).isRequired,
    selectedNotificationMessage: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }).isRequired,
    startDate: PropTypes.object.isRequired,
    startTime: PropTypes.object.isRequired
  }).isRequired,
  isEdit: PropTypes.bool.isRequired
};

export default OverviewSection;
